import type { AdGroupListItem } from "@models/adGroup.model";
import { AD_GROUP_NETWORKS, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";
import NetworkTag, { type NetworkLooks } from "./NetworkTag";

type Props = {
  adGroups: AdGroupListItem[];
  campaigns: { id: number; name: string }[];
  networkLooks?: NetworkLooks;
  isBusy: boolean;
  onOpen: (adGroupId: number) => void;
  onCreate: (launchId: number) => void;
};

/*
    "Needs review" counted `total - approved`, so a Do Not Use ad -- "rejected and not to
    be offered to this client again" (MPN-1612) -- read as work owed to Ops that nobody
    was ever going to do.

    That mislabelling also hid the publish bug beside it: the Publish page was blocking
    Motimatic ABM Q226 on the same three rejected ads, and "3 need review" here made that
    look like agreement rather than the contradiction it was.

    Stated as an exclusion rather than a list of the statuses that count, so a status
    added later is treated as outstanding until somebody decides otherwise. The same
    reasoning as publish_control_service, which answers this question for the Publish page.
*/
const SETTLED = ["APPROVED", "DO_NOT_USE"] as const;

function pillFor(group: AdGroupListItem) {
  const total = group.ad_count;
  const approved = group.ads_by_status.APPROVED ?? 0;
  const settled = SETTLED.reduce((n, status) => n + (group.ads_by_status[status] ?? 0), 0);
  const pending = total - settled;
  if (!group.audience_definition_id || total === 0) {
    return { className: "adgroup-pill adgroup-pill--warn", text: "Not configured" };
  }
  if (pending) {
    return {
      className: "adgroup-pill adgroup-pill--warn",
      text: `${pending} need${pending === 1 ? "s" : ""} review`,
    };
  }
  // Nothing outstanding and nothing to run: every ad here has been rejected. "All
  // approved" would be false and would read as ready to publish.
  if (!approved) {
    return { className: "adgroup-pill adgroup-pill--warn", text: "No ads in use" };
  }
  return { className: "adgroup-pill adgroup-pill--live", text: "All approved" };
}

/** Which networks the group runs on, as chosen: following, or its own list (MPN-1467). */
function NetworksIndicator({ group, looks }: { group: AdGroupListItem; looks: NetworkLooks }) {
  if (group.follow_campaign_networks) {
    return <span className="adgroup-row__networks">Follows campaign</span>;
  }
  return (
    <span className="adgroup-row__networks">
      {group.networks.map((network) => (
        <NetworkTag key={network} network={network} looks={looks} />
      ))}
    </span>
  );
}

export default function AdGroupsList({
  adGroups,
  campaigns,
  networkLooks = {},
  isBusy,
  onOpen,
  onCreate,
}: Props) {
  const showsNetworks = useFeatureFlag(AD_GROUP_NETWORKS);

  if (!campaigns.length) {
    return (
      <div className="adgroup-empty">
        <div className="adgroup-empty__title">No campaigns yet</div>
        <div className="adgroup-empty__body">
          An ad group belongs to a campaign. Create a campaign first, then come back here to add
          ad groups to it.
        </div>
      </div>
    );
  }

  const groupsByLaunch = new Map<number, AdGroupListItem[]>();
  for (const group of adGroups) {
    const list = groupsByLaunch.get(group.launch_id) ?? [];
    list.push(group);
    groupsByLaunch.set(group.launch_id, list);
  }

  return (
    <div className="adgroup-pane adgroup-pane--list">
      {adGroups.length === 0 && (
        <div className="small text-muted mb-3">
          An ad group holds the ads for one audience within a campaign.
        </div>
      )}

      {campaigns.map((campaign) => {
        const groups = groupsByLaunch.get(campaign.id) ?? [];
        return (
          <div key={campaign.id} className="adgroup-section">
            <div className="adgroup-section__head">
              <span className="adgroup-section__title">{campaign.name}</span>
              <span className="adgroup-section__count">{groups.length}</span>
              <button
                type="button"
                className="btn btn-primary btn-sm ms-auto"
                style={{ fontSize: "0.72rem" }}
                onClick={() => onCreate(campaign.id)}
                disabled={isBusy}
              >
                + Ad Group
              </button>
            </div>
            <div className="adgroup-section__body">
              {groups.length === 0 ? (
                <div className="adgroup-section__empty">No ad groups yet</div>
              ) : (
                <div className="adgroup-card">
                  {groups.map((group) => {
                    const pill = pillFor(group);
                    const audience = group.audience_name ?? "No audience assigned";
                    const ads = group.ad_count === 0 ? "No ads" : `${group.ad_count} ads`;
                    return (
                      <button
                        key={group.id}
                        type="button"
                        className="adgroup-row"
                        onClick={() => onOpen(group.id)}
                        disabled={isBusy}
                      >
                        <span className="adgroup-row__main">
                          <span className="adgroup-row__name">{group.name}</span>
                          <span className="adgroup-row__sub">
                            {audience} · {ads}
                          </span>
                          {showsNetworks && <NetworksIndicator group={group} looks={networkLooks} />}
                        </span>
                        <span className={pill.className}>{pill.text}</span>
                        <span className="adgroup-row__chev">›</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
